import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import DirectionsTruckIcon from "@mui/icons-material/LocalShipping";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import AssessmentIcon from "@mui/icons-material/Assessment";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import { StatCard } from "../../components/General/StatCard/StatCard";
import useAuth from "../../hooks/useAuth";
import { getRecruitingStats } from "../../api/recruitingStatsService";
import { DriverStatsDto } from "../../interfaces/Recruiting/recruitingStats";
import { getRecruitingContactStatistics } from "../../api/recruitingContactsService";
import { getActiveDrivers } from "../../api/driverService";
import { camelCaseToSentenceCase } from "../../helpers/camelCaseToSentenceCase";

const formatNumber = (value?: number) =>
  typeof value === "number" ? value.toLocaleString() : "--";

const formatPercentage = (value?: number) =>
  typeof value === "number" ? `${value.toFixed(1)}%` : "--";

const formatCurrency = (value?: number) =>
  typeof value === "number"
    ? value.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
      })
    : "--";

const getDriverCategoryLabel = (driverType?: string | null) => {
  const normalizedType = driverType?.replace(/[^a-z0-9]/gi, "").toLowerCase();

  switch (normalizedType) {
    case "cd":
    case "company":
      return "Company driver";
    case "oo":
    case "owner":
      return "Owner operator";
    default:
      return driverType ? camelCaseToSentenceCase(driverType) : "Uncategorized";
  }
};

const englishMonthNames = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

type DriverStatRowProps = {
  label: string;
  c1?: number;
  fox?: number;
};

const DriverStatRow = ({ label, c1, fox }: DriverStatRowProps) => (
  <>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="subtitle1" fontWeight={600} textAlign="right">
      {formatNumber(c1)}
    </Typography>
    <Typography variant="subtitle1" fontWeight={600} textAlign="right">
      {formatNumber(fox)}
    </Typography>
  </>
);

type DriverNetRowProps = {
  label: string;
  value?: number;
};

const DriverNetRow = ({ label, value }: DriverNetRowProps) => (
  <>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="subtitle1" fontWeight={700} textAlign="right">
      {formatNumber(value)}
    </Typography>
  </>
);

const RecruitingHomePage = () => {
  const { token } = useAuth();
  const [selectedDate, setSelectedDate] = useState(
    () => new Date().toISOString().split("T")[0],
  );
  const [startDate, setStartDate] = useState("");

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["recruitingStats", selectedDate, startDate],
    queryFn: () => {
      if (!token) throw new Error("No token");
      return getRecruitingStats(
        token,
        selectedDate || undefined,
        startDate || undefined,
      );
    },
    enabled: !!token,
  });

  const selectedDateObject = selectedDate ? new Date(selectedDate) : new Date();
  const statsYear = selectedDateObject.getFullYear();
  const statsMonth = selectedDateObject.getMonth() + 1;

  const {
    data: contactStatistics,
    isError: isContactStatsError,
    error: contactStatsError,
  } = useQuery({
    queryKey: ["recruitingContactStatistics", statsYear, statsMonth],
    queryFn: () => {
      if (!token) {
        throw new Error("No token");
      }

      return getRecruitingContactStatistics(token, statsYear, statsMonth);
    },
    enabled: !!token,
  });

  const {
    data: activeDrivers,
    isLoading: areActiveDriversLoading,
    isError: isActiveDriversError,
  } = useQuery({
    queryKey: ["drivers", true],
    queryFn: () => {
      if (!token) throw new Error("No token");
      return getActiveDrivers(token);
    },
    enabled: !!token,
  });

  const driverStats: DriverStatsDto | undefined = data?.driverStats;
  const truckStats = data?.truckStats;
  const orientationExpenseStats = data?.orientationExpenseStats;
  const activeDriversByCategory = activeDrivers?.reduce<Record<string, number>>(
    (counts, driver) => {
      const category = getDriverCategoryLabel(driver.driverType);
      counts[category] = (counts[category] ?? 0) + 1;
      return counts;
    },
    { "Company driver": 0, "Owner operator": 0 },
  );
  const orderedActiveDriverCategories = Object.entries(
    activeDriversByCategory ?? {},
  ).sort(([left], [right]) => {
    const categoryOrder = ["Company driver", "Owner operator"];
    const leftIndex = categoryOrder.indexOf(left);
    const rightIndex = categoryOrder.indexOf(right);

    if (leftIndex === -1 && rightIndex === -1) return left.localeCompare(right);
    if (leftIndex === -1) return 1;
    if (rightIndex === -1) return -1;
    return leftIndex - rightIndex;
  });

  const applicantsPeriodTitle = contactStatistics
    ? `${englishMonthNames[statsMonth - 1]} ${statsYear}`
    : "Period";

  return (
    <Box
      minHeight="100vh"
      sx={{
        background: (theme) => theme.palette.grey[50],
        px: { xs: 2, md: 4 },
        py: { xs: 3, md: 4 },
      }}
    >
      <Stack spacing={3}>
        <Box
          display="flex"
          flexDirection={{ xs: "column", md: "row" }}
          alignItems={{ xs: "flex-start", md: "center" }}
          justifyContent="space-between"
          gap={2}
        >
          <Box>
            <Typography variant="h4" fontWeight={700}>
              Recruiting Overview
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Snapshot of driver and truck activity
            </Typography>
          </Box>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              label="As of"
              type="date"
              value={selectedDate}
              onChange={(event) => setSelectedDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: { xs: 0, sm: 200 } }}
            />
            <TextField
              label="Start date"
              type="date"
              value={startDate}
              onChange={(event) => setStartDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="Optional"
              sx={{ minWidth: { xs: 0, sm: 200 } }}
            />
          </Stack>
        </Box>

        {isLoading ? (
          <Box
            flexWrap={{ xs: "wrap", lg: "nowrap" }}
            display="flex"
            justifyContent="center"
            py={6}
          >
            <CircularProgress />
          </Box>
        ) : isError ? (
          <Alert severity="error">
            {(error as Error)?.message || "Unable to load recruiting stats."}
          </Alert>
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12} md={7}>
              <Paper elevation={4} sx={{ p: 3, borderRadius: 3 }}>
                <Stack spacing={2}>
                  <Box
                    flexWrap={{ xs: "wrap", lg: "nowrap" }}
                    display="flex"
                    alignItems="center"
                    gap={1}
                  >
                    <PeopleAltIcon color="primary" />
                    <Typography variant="h6" fontWeight={700}>
                      Driver Stats
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      bgcolor: "action.hover",
                      border: 1,
                      borderColor: "divider",
                      borderRadius: 2,
                      p: 2,
                    }}
                  >
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      alignItems={{ xs: "flex-start", sm: "center" }}
                      justifyContent="space-between"
                      spacing={2}
                    >
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Total Active Drivers
                        </Typography>
                        <Typography variant="h3" fontWeight={700}>
                          {areActiveDriversLoading
                            ? "--"
                            : formatNumber(activeDrivers?.length)}
                        </Typography>
                      </Box>
                      <Stack
                        direction="row"
                        flexWrap="wrap"
                        useFlexGap
                        spacing={{ xs: 3, sm: 5 }}
                        aria-label="Active drivers by category"
                      >
                        {orderedActiveDriverCategories.map(
                          ([category, count]) => (
                            <Box key={category}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {category}
                              </Typography>
                              <Typography variant="h6" fontWeight={700}>
                                {formatNumber(count)}
                              </Typography>
                            </Box>
                          ),
                        )}
                      </Stack>
                    </Stack>
                    {isActiveDriversError ? (
                      <Alert severity="warning" sx={{ mt: 2 }}>
                        Unable to load the current active-driver summary.
                      </Alert>
                    ) : null}
                  </Box>
                  <Box
                    display="grid"
                    gridTemplateColumns="2fr 1fr 1fr"
                    gap={1}
                    alignItems="center"
                  >
                    <Typography variant="caption" color="text.secondary">
                      Metric
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      textAlign="right"
                    >
                      C1
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      textAlign="right"
                    >
                      Fox
                    </Typography>
                    <Divider sx={{ gridColumn: "1 / -1" }} />
                    <DriverStatRow
                      label="MTD Hires"
                      c1={driverStats?.mtdHiresC1}
                      fox={driverStats?.mtdHiresFox}
                    />
                    <DriverStatRow
                      label="YTD Hires"
                      c1={driverStats?.ytdHiresC1}
                      fox={driverStats?.ytdHiresFox}
                    />
                    <DriverStatRow
                      label="MTD Terminations"
                      c1={driverStats?.mtdTerminationsC1}
                      fox={driverStats?.mtdTerminationsFox}
                    />
                    <DriverStatRow
                      label="YTD Terminations"
                      c1={driverStats?.ytdTerminationsC1}
                      fox={driverStats?.ytdTerminationsFox}
                    />
                  </Box>
                  <Divider />
                  <Box
                    display="grid"
                    gridTemplateColumns="2fr 1fr"
                    gap={1}
                    alignItems="center"
                  >
                    <DriverNetRow
                      label="MTD Driver Net"
                      value={driverStats?.mtdDriverNet}
                    />
                    <DriverNetRow
                      label="YTD Driver Net"
                      value={driverStats?.ytdDriverNet}
                    />
                  </Box>
                </Stack>
              </Paper>
            </Grid>
            <Grid item xs={12} md={5}>
              <Paper elevation={4} sx={{ p: 3, borderRadius: 3 }}>
                <Stack spacing={2}>
                  <Box
                    flexWrap={{ xs: "wrap", lg: "nowrap" }}
                    display="flex"
                    alignItems="center"
                    gap={1}
                  >
                    <DirectionsTruckIcon color="primary" />
                    <Typography variant="h6" fontWeight={700}>
                      Truck Stats
                    </Typography>
                  </Box>
                  <Box
                    display="grid"
                    gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }}
                    gap={2}
                  >
                    <StatCard
                      title="Seated Trucks (C1)"
                      value={formatNumber(truckStats?.seatedTrucksC1)}
                      icon={<DirectionsTruckIcon />}
                      color="#1976d2"
                    />
                    <StatCard
                      title="Seated Trucks (Fox)"
                      value={formatNumber(truckStats?.seatedTrucksFox)}
                      icon={<DirectionsTruckIcon />}
                      color="#00897b"
                    />
                    <StatCard
                      title="Available Trucks (C1)"
                      value={formatNumber(truckStats?.availableTrucksC1)}
                      icon={<EventAvailableIcon />}
                      color="#f9a825"
                    />
                    <StatCard
                      title="Available Trucks (Fox)"
                      value={formatNumber(truckStats?.availableTrucksFox)}
                      icon={<EventAvailableIcon />}
                      color="#fb8c00"
                    />
                    <StatCard
                      title="Utilization"
                      value={formatPercentage(
                        truckStats?.utilizationPercentage,
                      )}
                      icon={<AssessmentIcon />}
                      color="#6a1b9a"
                    />
                  </Box>
                </Stack>
              </Paper>
            </Grid>
            <Grid item xs={12}>
              <Paper elevation={4} sx={{ p: 3, borderRadius: 3 }}>
                <Stack spacing={2}>
                  <Box
                    flexWrap={{ xs: "wrap", lg: "nowrap" }}
                    display="flex"
                    alignItems="center"
                    gap={1}
                  >
                    <AttachMoneyIcon color="primary" />
                    <Typography variant="h6" fontWeight={700}>
                      Expense Stats
                    </Typography>
                  </Box>
                  <Box
                    display="grid"
                    gridTemplateColumns={{ xs: "1fr", md: "1fr 2fr" }}
                    gap={3}
                  >
                    <StatCard
                      title="Total Expenses"
                      value={formatCurrency(
                        orientationExpenseStats?.totalExpenses,
                      )}
                      icon={<AttachMoneyIcon />}
                      color="#2e7d32"
                    />
                    <Stack spacing={1}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Expense Breakdown
                      </Typography>
                      <Stack spacing={1}>
                        {orientationExpenseStats?.expenseTypeBreakdown
                          ?.length ? (
                          orientationExpenseStats.expenseTypeBreakdown.map(
                            (expense) => (
                              <Box
                                flexWrap={{ xs: "wrap", lg: "nowrap" }}
                                key={expense.expenseType}
                                display="flex"
                                justifyContent="space-between"
                                gap={2}
                              >
                                <Typography variant="body2">
                                  {expense.expenseType}
                                </Typography>
                                <Typography variant="body2" fontWeight={600}>
                                  {formatCurrency(expense.totalCost)}
                                </Typography>
                              </Box>
                            ),
                          )
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No expenses available for this period.
                          </Typography>
                        )}
                      </Stack>
                    </Stack>
                  </Box>
                </Stack>
              </Paper>
            </Grid>
            <Grid item xs={12}>
              <Paper elevation={4} sx={{ p: 3, borderRadius: 3 }}>
                <Stack spacing={2}>
                  <Box
                    flexWrap={{ xs: "wrap", lg: "nowrap" }}
                    display="flex"
                    alignItems="center"
                    gap={1}
                  >
                    <AssignmentIndIcon color="primary" />
                    <Typography variant="h6" fontWeight={700}>
                      Applicants Statistics
                    </Typography>
                  </Box>
                  {isContactStatsError ? (
                    <Alert severity="error">
                      {(contactStatsError as Error)?.message ||
                        "Unable to load applicant statistics."}
                    </Alert>
                  ) : (
                    <>
                      <Box
                        display="grid"
                        gridTemplateColumns={{ xs: "1fr", md: "1fr 2fr" }}
                        gap={3}
                      >
                        <StatCard
                          title={applicantsPeriodTitle}
                          value={formatNumber(contactStatistics?.totalContacts)}
                          icon={<AssignmentIndIcon />}
                          color="#00695c"
                        />
                        <Stack spacing={1}>
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                          >
                            Status Breakdown
                          </Typography>
                          {contactStatistics?.statuses?.length ? (
                            contactStatistics.statuses.map((statusStat) => (
                              <Box key={statusStat.status}>
                                <Typography variant="body2" fontWeight={700}>
                                  {statusStat.status}:{" "}
                                  {formatNumber(statusStat.count)}
                                </Typography>
                                {statusStat.reasons?.length ? (
                                  <Stack pl={2} spacing={0.5}>
                                    {statusStat.reasons.map((reason) => (
                                      <Box
                                        flexWrap={{ xs: "wrap", lg: "nowrap" }}
                                        key={`${statusStat.status}-${reason.reason}`}
                                        display="flex"
                                        justifyContent="space-between"
                                      >
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                        >
                                          {reason.reason}
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          fontWeight={600}
                                        >
                                          {formatNumber(reason.count)}
                                        </Typography>
                                      </Box>
                                    ))}
                                  </Stack>
                                ) : null}
                              </Box>
                            ))
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              No applicants data available for this period.
                            </Typography>
                          )}
                        </Stack>
                      </Box>
                    </>
                  )}
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        )}
      </Stack>
    </Box>
  );
};

export default RecruitingHomePage;
